import React, { FormEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  isSignInWithEmailLink,
  sendEmailVerification,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signOut,
} from 'firebase/auth';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faEnvelope,
  faEye,
  faEyeSlash,
  faLink,
  faLock,
  faMoon,
  faSun,
  faWallet,
} from '@fortawesome/free-solid-svg-icons';
import { auth } from './Auth';
import { appContext } from '../../App';
import ConfirmationModal from '../Elements/ConfirmationModal';
import TOTPVerification from './TOTPVerification';

interface TOTPCheckResponse {
  requiresTOTP: boolean;
  message?: string;
}

type SignInMethod = 'password' | 'magic-link' | 'wallet';

const SIGN_IN_METHODS: { method: SignInMethod; label: string }[] = [
  { method: 'password', label: 'Password' },
  { method: 'magic-link', label: 'Magic Link' },
  { method: 'wallet', label: 'Wallet' },
];

// Mainnet BTC address shapes: legacy/P2SH base58 (1…, 3…) or bech32 (bc1…). The miner page reports unknown addresses itself.
const BTC_ADDRESS_PATTERN = /^([13][a-km-zA-HJ-NP-Z1-9]{25,34}|bc1[02-9ac-hj-np-z]{11,71})$/;

// bech32 is case-insensitive but only valid in one case, so it is compared lowercase; base58 is case-sensitive.
const normalizeBtcAddress = (value: string): string => {
  const trimmed = value.trim();
  return trimmed.toLowerCase().startsWith('bc1') ? trimmed.toLowerCase() : trimmed;
};

const MAGIC_LINK_EMAIL_KEY = 'segpool.login.email';
const MAGIC_LINK_NEXT_URL_KEY = 'segpool.login.next';
const MAGIC_LINK_REDIRECT_URL = process.env.REACT_APP_MAGIC_LINK_URL?.trim() ?? '';
const LOCAL_MAGIC_LINK_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);

const getSafeNextUrl = (search: string): string | null => {
  try {
    const params = new URLSearchParams(search);
    const next = params.get('next');
    if (!next || !next.trim()) {
      return null;
    }

    const target = new URL(next, window.location.origin);
    if (target.origin !== window.location.origin) {
      return null;
    }

    return target.toString();
  } catch {
    return null;
  }
};

const getStoredMagicLinkNextUrl = (): string | null => {
  const storedNext = window.localStorage.getItem(MAGIC_LINK_NEXT_URL_KEY);
  if (!storedNext) {
    return null;
  }

  try {
    const target = new URL(storedNext, window.location.origin);
    if (target.origin !== window.location.origin) {
      return null;
    }

    return target.toString();
  } catch {
    return null;
  }
};

const buildMagicLinkTargetUrl = (nextUrl?: string | null): string => {
  const targetUrl = MAGIC_LINK_REDIRECT_URL
    ? new URL(MAGIC_LINK_REDIRECT_URL, window.location.origin)
    : new URL('/login', window.location.origin);

  if (!targetUrl.pathname || targetUrl.pathname === '/') {
    targetUrl.pathname = '/login';
  }

  if (nextUrl) {
    targetUrl.searchParams.set('next', nextUrl);
  } else {
    targetUrl.searchParams.delete('next');
  }

  return targetUrl.toString();
};

const isLocalMagicLinkTarget = (url: string): boolean => {
  try {
    return LOCAL_MAGIC_LINK_HOSTS.has(new URL(url).hostname);
  } catch {
    return false;
  }
};

const getFirebaseErrorDetails = (error: any): string => {
  const parts = [
    error?.code,
    error?.message,
    error?.customData?._serverResponse,
    error?.customData?._tokenResponse?.error?.message,
    error?.response?.data?.error?.message,
    error?.error?.message,
  ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

  if (parts.length > 0) {
    return parts.join(' | ');
  }

  try {
    return JSON.stringify(error);
  } catch {
    return '';
  }
};

const getMagicLinkErrorMessage = (error: any, targetUrl: string): string => {
  const errorDetails = getFirebaseErrorDetails(error);

  if (error?.code === 'auth/too-many-requests' || errorDetails.includes('QUOTA_EXCEEDED') || errorDetails.includes('Exceeded daily quota for email sign-in')) {
    return 'Firebase has reached its daily quota for email-link sign-ins. Try again after the quota resets, or use password sign-in for now.';
  }

  if (error?.code === 'auth/operation-not-allowed') {
    return 'Magic link sign-in is not enabled in Firebase yet.';
  }

  if (error?.code === 'auth/invalid-continue-uri' || error?.code === 'auth/unauthorized-continue-uri') {
    const hostname = new URL(targetUrl).hostname;
    const targetLabel = isLocalMagicLinkTarget(targetUrl) ? 'localhost' : hostname;
    return `Firebase rejected the magic link redirect URL. Add ${targetLabel} to Firebase Authentication > Settings > Authorized domains${MAGIC_LINK_REDIRECT_URL ? '.' : ' or set REACT_APP_MAGIC_LINK_URL to an authorized /login URL.'}`;
  }

  if (error?.code === 'auth/invalid-dynamic-link-domain') {
    return 'Firebase rejected the configured magic link domain. Check the email link configuration for this project.';
  }

  return 'Unable to send a magic link right now. Please try again.';
};

const SignInScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { darkMode, setDarkMode, setIsTotpVerified } = useContext(appContext);

  const nextAfterLogin = useMemo(() => getSafeNextUrl(location.search), [location.search]);
  const magicLinkTargetUrl = useMemo(() => buildMagicLinkTargetUrl(nextAfterLogin), [nextAfterLogin]);

  const [activeMethod, setActiveMethod] = useState<SignInMethod>('password');
  const [isLoading, setIsLoading] = useState(false);
  const [isMagicLinkLoading, setIsMagicLinkLoading] = useState(false);
  const [isCompletingMagicLink, setIsCompletingMagicLink] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [pendingMagicEmail, setPendingMagicEmail] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [openResetModal, setOpenResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetUsername, setResetUsername] = useState('');
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [requiresTOTP, setRequiresTOTP] = useState(false);
  const [userEmailForTOTP, setUserEmailForTOTP] = useState('');
  const [idToken, setIdToken] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  const isBlockingAuthFlow = isCompletingMagicLink;

  const continueAfterLogin = useCallback(() => {
    const redirectTarget = nextAfterLogin || getStoredMagicLinkNextUrl();
    window.localStorage.removeItem(MAGIC_LINK_NEXT_URL_KEY);

    if (redirectTarget) {
      window.location.replace(redirectTarget);
      return;
    }

    navigate('/');
  }, [navigate, nextAfterLogin]);

  const handleSignedInUser = useCallback(async (email: string | null, token: string, fallbackEmail: string) => {
    const totpCheckResponse = await axios.get<TOTPCheckResponse>(
      '/pool/api/totp/check',
      {
        params: { email },
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    if (totpCheckResponse.data.requiresTOTP) {
      setUserEmailForTOTP(email || fallbackEmail);
      setIdToken(token);
      setRequiresTOTP(true);
      return;
    }

    continueAfterLogin();
  }, [continueAfterLogin]);

  useEffect(() => {
    if (nextAfterLogin) {
      window.localStorage.setItem(MAGIC_LINK_NEXT_URL_KEY, nextAfterLogin);
      return;
    }

    if (!isSignInWithEmailLink(auth, window.location.href)) {
      window.localStorage.removeItem(MAGIC_LINK_NEXT_URL_KEY);
    }
  }, [nextAfterLogin]);

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, username, password);
      const user = userCredential.user;
      const token = await user.getIdToken();

      if (!user.emailVerified) {
        await signOut(auth);
        setShowVerificationModal(true);
        setIsLoading(false);
        return;
      }

      await handleSignedInUser(user.email, token, username);
    } catch (error: any) {
      console.error('Login error:', error);
      setErrorMessage(
        error?.code === 'auth/wrong-password' || error?.code === 'auth/user-not-found' || error?.code === 'auth/invalid-credential'
          ? 'Invalid email or password'
          : 'Login failed. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleWalletSignIn = (event: FormEvent): void => {
    event.preventDefault();
    const address = normalizeBtcAddress(walletAddress);
    if (!BTC_ADDRESS_PATTERN.test(address)) {
      setSuccessMessage(null);
      setErrorMessage('Enter a valid Bitcoin wallet address.');
      return;
    }

    navigate(`/miner/${encodeURIComponent(address)}`);
  };

  const handleSendMagicLink = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setIsMagicLinkLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      if (nextAfterLogin) {
        window.localStorage.setItem(MAGIC_LINK_NEXT_URL_KEY, nextAfterLogin);
      } else {
        window.localStorage.removeItem(MAGIC_LINK_NEXT_URL_KEY);
      }

      await sendSignInLinkToEmail(auth, pendingMagicEmail, {
        url: magicLinkTargetUrl,
        handleCodeInApp: true,
      });

      window.localStorage.setItem(MAGIC_LINK_EMAIL_KEY, pendingMagicEmail);
      setSuccessMessage(`Magic link sent to ${pendingMagicEmail}. Open it on this device to continue.`);
    } catch (error: any) {
      console.error('Unable to send magic link', { error, magicLinkTargetUrl });
      setErrorMessage(getMagicLinkErrorMessage(error, magicLinkTargetUrl));
    } finally {
      setIsMagicLinkLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (!username || !password) {
      setErrorMessage('Please enter your email and password to resend verification email.');
      return;
    }

    setResendLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, username, password);
      await sendEmailVerification(userCredential.user);
      await signOut(auth);
      setSuccessMessage('Verification email resent!');
      setShowVerificationModal(false);
    } catch (error) {
      console.error('Error resending email:', error);
      setErrorMessage('Failed to resend email. Please try again later.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleResetPassword = async (): Promise<void> => {
    if (!resetEmail) {
      setErrorMessage('Please enter your email address.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post('/pool/auth/resetpassword', { email: resetEmail, username: resetUsername });
      if (response.data.status) {
        setSuccessMessage(response.data.message || 'Password reset email sent. Please check your inbox.');
        await sendPasswordResetEmail(auth, resetEmail);
        setOpenResetModal(false);
      } else {
        setErrorMessage(response.data.message || 'Failed to send reset email. Ensure the email is correct.');
      }
    } catch (error: any) {
      console.error('Reset password error:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to send reset email. Ensure the email is correct.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) {
      return;
    }

    const storedEmail = window.localStorage.getItem(MAGIC_LINK_EMAIL_KEY) || username || pendingMagicEmail;
    if (!storedEmail) {
      setActiveMethod('magic-link');
      setErrorMessage('Enter the same email address you used for the magic link to finish signing in.');
      return;
    }

    let cancelled = false;

    const completeMagicLink = async () => {
      setIsCompletingMagicLink(true);
      setIsMagicLinkLoading(true);
      setErrorMessage(null);
      setSuccessMessage(null);

      try {
        const credential = await signInWithEmailLink(auth, storedEmail, window.location.href);
        window.localStorage.removeItem(MAGIC_LINK_EMAIL_KEY);

        if (!cancelled) {
          const token = await credential.user.getIdToken();
          await handleSignedInUser(credential.user.email, token, storedEmail);
        }
      } catch (error) {
        if (!cancelled) {
          setErrorMessage('Unable to complete magic link sign-in. Request a new link and try again.');
        }
      } finally {
        if (!cancelled) {
          setIsCompletingMagicLink(false);
          setIsMagicLinkLoading(false);
        }
      }
    };

    completeMagicLink();

    return () => {
      cancelled = true;
    };
  }, [handleSignedInUser, pendingMagicEmail, username]);

  const handleCloseSnackbar = (): void => {
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-white dark:bg-slate-950 transition-colors duration-500 overflow-hidden">
      <div className="hidden md:flex md:w-1/2 lg:w-3/5 bg-[#0c2a4a] relative overflow-hidden items-center justify-center p-12">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-500/10 rounded-full blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-blue-400/10 rounded-full blur-[150px] animate-pulse"></div>

        <div className="relative z-10 max-w-lg text-center">
          <img src="/assets/segpool.png" alt="SegPool Logo" className="h-20 mx-auto mb-3 drop-shadow-2xl brightness-110" />
          <h1 className="text-4xl lg:text-5xl font-black text-white mb-6 tracking-tight">
            Elevate Your <span className="text-blue-400">Mining</span> Experience
          </h1>
          <p className="text-blue-100/70 text-lg lg:text-xl font-medium leading-relaxed">
            The most powerful and transparent mining pool for professional miners.
            Real-time analytics, daily payouts, and sub-account management.
          </p>
        </div>
      </div>

      <div className="flex-1 flex flex-col relative bg-white dark:bg-slate-950/40 overflow-y-auto">
        <div className="p-4 md:p-6 flex items-center z-50">
          <div className="flex-1 flex justify-end gap-2">
            <Link
              to="/"
              className="p-3 bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-400 rounded-2xl hover:bg-slate-200 dark:hover:bg-slate-800 transition-all border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-center"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="text-lg" />
            </Link>
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="p-3 bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-400 rounded-2xl hover:bg-slate-200 dark:hover:bg-slate-800 transition-all border border-slate-200 dark:border-slate-800 shadow-sm"
            >
              <FontAwesomeIcon icon={darkMode ? faSun : faMoon} className="text-lg" />
            </button>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-6 md:p-12 lg:p-20">
          {requiresTOTP ? (
            <div className="w-full max-w-md animate-in zoom-in-95 duration-500">
              <TOTPVerification
                email={userEmailForTOTP}
                idToken={idToken}
                onVerify={() => {
                  setIsTotpVerified(true);
                  continueAfterLogin();
                }}
                onCancel={() => {
                  setRequiresTOTP(false);
                  auth.signOut();
                }}
              />
            </div>
          ) : isBlockingAuthFlow ? (
            <div className="w-full max-w-md animate-in fade-in zoom-in-95 duration-500">
              <div className="rounded-[2rem] border border-slate-200 bg-white/95 p-8 shadow-2xl shadow-slate-200/70 dark:border-slate-800 dark:bg-slate-900/80 dark:shadow-black/30 md:p-10">
                <div className="flex flex-col items-center text-center" role="status" aria-live="polite">
                  <img
                    src="/assets/segmentslogo.png"
                    alt="SegPool Logo"
                    className="mb-6 h-14 drop-shadow-2xl brightness-110"
                  />
                  <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
                    <div className="h-7 w-7 rounded-full border-[3px] border-current/20 border-t-current animate-spin" />
                  </div>
                  <h3 className="text-2xl font-black tracking-tight text-[#0c2a4a] dark:text-white">
                    Processing your magic link
                  </h3>
                  <p className="mt-3 max-w-sm text-sm font-medium leading-relaxed text-slate-500 dark:text-slate-400">
                    Please wait while we verify your email link and finish signing you in.
                  </p>
                  <div className="mt-6 h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                    <div className="auth-progress-bar h-full w-1/3 rounded-full bg-gradient-to-r from-cyan-400 via-blue-500 to-[#0A3A6B]" />
                  </div>
                  <p className="mt-4 text-[11px] font-black uppercase tracking-[0.28em] text-slate-400 dark:text-slate-500">
                    Please keep this tab open
                  </p>
                </div>
              </div>
              <style>{`
                @keyframes auth-progress-slide {
                  0% { transform: translateX(-130%); }
                  100% { transform: translateX(320%); }
                }

                .auth-progress-bar {
                  animation: auth-progress-slide 1.4s infinite ease-in-out;
                }
              `}</style>
            </div>
          ) : (
            <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom-8 duration-700">
              {!openResetModal ? (
                <>
                  <div className="mb-6 md:mb-10 text-center md:text-left">
                    <img
                      src="/assets/segmentslogo.png"
                      alt="SegPool Logo"
                      className="h-14 mx-auto mb-4 drop-shadow-2xl brightness-110 md:hidden"
                    />
                    <h2 className="text-3xl md:text-4xl font-black text-[#0c2a4a] dark:text-white mb-2 tracking-tight">Welcome Back</h2>
                    <p className="text-slate-500 dark:text-slate-400 font-medium text-[10px] md:text-base uppercase tracking-widest opacity-80">
                      Please enter your details to sign in.
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-2 md:gap-3 mb-6">
                    {SIGN_IN_METHODS.map(({ method, label }) => (
                      <button
                        key={method}
                        type="button"
                        onClick={() => {
                          setActiveMethod(method);
                          setOpenResetModal(false);
                        }}
                        className={`rounded-2xl border px-2 md:px-4 py-3 text-[10px] md:text-sm font-black uppercase tracking-wider md:tracking-widest transition-all ${
                          activeMethod === method
                            ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-500/10 dark:text-blue-300'
                            : 'border-slate-200 text-slate-500 hover:border-slate-300 dark:border-slate-800 dark:text-slate-400'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  {activeMethod === 'password' && (
                    <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                      <div className="space-y-1 md:space-y-2">
                        <label className="text-[10px] md:text-xs font-black text-slate-500 dark:text-slate-400 ml-1 uppercase tracking-widest" htmlFor="email">
                          Email Address
                        </label>
                        <div className="relative group">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-500 transition-colors">
                            <FontAwesomeIcon icon={faEnvelope} className="text-xs md:text-sm" />
                          </div>
                          <input
                            className="w-full pl-11 pr-4 py-3 md:py-4 bg-slate-50 dark:bg-slate-900/50 text-slate-800 dark:text-white rounded-2xl border border-slate-200 dark:border-slate-800 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all font-medium text-sm md:text-base placeholder:text-slate-400"
                            type="email"
                            id="email"
                            value={username}
                            onChange={(event) => setUsername(event.target.value)}
                            required
                            placeholder="name@example.com"
                          />
                        </div>
                      </div>

                      <div className="space-y-1 md:space-y-2">
                        <div className="flex justify-between items-center px-1">
                          <label className="text-[10px] md:text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest" htmlFor="password">
                            Password
                          </label>
                          <button
                            type="button"
                            className="text-[10px] md:text-xs font-black text-blue-600 dark:text-blue-400 hover:underline tracking-widest"
                            onClick={() => setOpenResetModal(true)}
                          >
                            FORGOT PASSWORD?
                          </button>
                        </div>
                        <div className="relative group">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-500 transition-colors">
                            <FontAwesomeIcon icon={faLock} className="text-xs md:text-sm" />
                          </div>
                          <input
                            className="w-full pl-11 pr-12 py-3 md:py-4 bg-slate-50 dark:bg-slate-900/50 text-slate-800 dark:text-white rounded-2xl border border-slate-200 dark:border-slate-800 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all font-medium text-sm md:text-base placeholder:text-slate-400"
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            required
                            placeholder="********"
                          />
                          <button
                            type="button"
                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="text-xs md:text-sm" />
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3 px-1 pt-1 md:pt-0">
                        <input
                          id="remember"
                          type="checkbox"
                          className="w-4 h-4 md:w-5 md:h-5 rounded-lg border-slate-300 dark:border-slate-700 text-blue-600 focus:ring-blue-500 bg-white dark:bg-slate-900 transition-all"
                        />
                        <label htmlFor="remember" className="text-[10px] md:text-sm font-bold text-slate-600 dark:text-slate-400 cursor-pointer select-none">
                          Remember this device
                        </label>
                      </div>

                      <button
                        type="submit"
                        disabled={isLoading || isMagicLinkLoading}
                        className="w-full py-3.5 md:py-4 bg-[#0A3A6B] hover:bg-[#0c4a8a] text-white rounded-2xl font-black text-base md:text-lg shadow-xl shadow-blue-500/10 hover:shadow-blue-500/20 active:scale-[0.98] transition-all disabled:opacity-70 disabled:scale-100 flex items-center justify-center gap-3 mt-4 md:mt-8 h-[54px] md:h-[60px]"
                      >
                        {isLoading ? (
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            <span className="text-sm md:text-base tracking-widest uppercase">LOGGING IN...</span>
                          </div>
                        ) : (
                          <>
                            <span>SIGN IN</span>
                            <FontAwesomeIcon icon={faArrowRight} className="text-xs opacity-70" />
                          </>
                        )}
                      </button>
                    </form>
                  )}

                  {activeMethod === 'magic-link' && (
                    <form onSubmit={handleSendMagicLink} className="space-y-4 md:space-y-6">
                      <div className="space-y-1 md:space-y-2">
                        <label className="text-[10px] md:text-xs font-black text-slate-500 dark:text-slate-400 ml-1 uppercase tracking-widest" htmlFor="magic-link-email">
                          Email Address
                        </label>
                        <div className="relative group">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-500 transition-colors">
                            <FontAwesomeIcon icon={faLink} className="text-xs md:text-sm" />
                          </div>
                          <input
                            className="w-full pl-11 pr-4 py-3 md:py-4 bg-slate-50 dark:bg-slate-900/50 text-slate-800 dark:text-white rounded-2xl border border-slate-200 dark:border-slate-800 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all font-medium text-sm md:text-base placeholder:text-slate-400"
                            type="email"
                            id="magic-link-email"
                            value={pendingMagicEmail}
                            onChange={(event) => setPendingMagicEmail(event.target.value)}
                            required
                            placeholder="name@example.com"
                          />
                        </div>
                        <p className="px-1 text-[11px] font-medium text-slate-500 dark:text-slate-400">
                          We'll send you a secure sign-in link for this device.
                        </p>
                      </div>

                      <button
                        type="submit"
                        disabled={isLoading || isMagicLinkLoading}
                        className="w-full py-3.5 md:py-4 bg-[#0A3A6B] hover:bg-[#0c4a8a] text-white rounded-2xl font-black text-base md:text-lg shadow-xl shadow-blue-500/10 hover:shadow-blue-500/20 active:scale-[0.98] transition-all disabled:opacity-70 disabled:scale-100 flex items-center justify-center gap-3 mt-4 md:mt-8 h-[54px] md:h-[60px]"
                      >
                        {isMagicLinkLoading ? (
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            <span className="text-sm md:text-base tracking-widest uppercase">SENDING LINK...</span>
                          </div>
                        ) : (
                          <>
                            <span>SEND MAGIC LINK</span>
                            <FontAwesomeIcon icon={faArrowRight} className="text-xs opacity-70" />
                          </>
                        )}
                      </button>
                    </form>
                  )}

                  {activeMethod === 'wallet' && (
                    <form onSubmit={handleWalletSignIn} className="space-y-4 md:space-y-6">
                      <div className="space-y-1 md:space-y-2">
                        <label className="text-[10px] md:text-xs font-black text-slate-500 dark:text-slate-400 ml-1 uppercase tracking-widest" htmlFor="wallet-address">
                          BTC Wallet Address
                        </label>
                        <div className="relative group">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-500 transition-colors">
                            <FontAwesomeIcon icon={faWallet} className="text-xs md:text-sm" />
                          </div>
                          <input
                            className="w-full pl-11 pr-4 py-3 md:py-4 bg-slate-50 dark:bg-slate-900/50 text-slate-800 dark:text-white rounded-2xl border border-slate-200 dark:border-slate-800 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all font-mono text-sm md:text-base placeholder:text-slate-400 placeholder:font-sans"
                            type="text"
                            id="wallet-address"
                            value={walletAddress}
                            onChange={(event) => setWalletAddress(event.target.value)}
                            required
                            autoComplete="off"
                            autoCapitalize="off"
                            autoCorrect="off"
                            spellCheck={false}
                            placeholder="bc1q..."
                          />
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={isLoading || isMagicLinkLoading}
                        className="w-full py-3.5 md:py-4 bg-[#0A3A6B] hover:bg-[#0c4a8a] text-white rounded-2xl font-black text-base md:text-lg shadow-xl shadow-blue-500/10 hover:shadow-blue-500/20 active:scale-[0.98] transition-all disabled:opacity-70 disabled:scale-100 flex items-center justify-center gap-3 mt-4 md:mt-8 h-[54px] md:h-[60px]"
                      >
                        <span>VIEW DASHBOARD</span>
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs opacity-70" />
                      </button>
                    </form>
                  )}

                  <div className="mt-8 md:mt-12 text-center">
                    <p className="text-slate-500 dark:text-slate-400 font-bold text-[10px] md:text-sm uppercase tracking-widest">
                      Don't have an account?{' '}
                      <button
                        onClick={() => navigate(`/signup${location.search}`)}
                        className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1 font-black"
                      >
                        Create account
                      </button>
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-6 md:mb-10 text-center md:text-left">
                    <h2 className="text-2xl md:text-3xl font-black text-[#0c2a4a] dark:text-white mb-2">Reset Password</h2>
                    <p className="text-slate-500 dark:text-slate-400 font-medium text-xs md:text-base">Enter your email and we'll send you a recovery link.</p>
                  </div>

                  <div className="space-y-4 md:space-y-6">
                    <div className="space-y-1 md:space-y-2">
                      <label className="text-[10px] md:text-xs font-black text-slate-500 dark:text-slate-400 ml-1 uppercase tracking-widest">
                        Username
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 md:py-4 bg-slate-50 dark:bg-slate-900/50 text-slate-800 dark:text-white rounded-2xl border border-slate-200 dark:border-slate-800 focus:border-blue-500 outline-none transition-all font-medium text-sm md:text-base"
                        placeholder="Your username"
                        value={resetUsername}
                        onChange={(event) => setResetUsername(event.target.value)}
                      />
                    </div>

                    <div className="space-y-1 md:space-y-2">
                      <label className="text-[10px] md:text-xs font-black text-slate-500 dark:text-slate-400 ml-1 uppercase tracking-widest">
                        Email Address
                      </label>
                      <input
                        type="email"
                        className="w-full px-4 py-3 md:py-4 bg-slate-50 dark:bg-slate-900/50 text-slate-800 dark:text-white rounded-2xl border border-slate-200 dark:border-slate-800 focus:border-blue-500 outline-none transition-all font-medium text-sm md:text-base"
                        placeholder="name@example.com"
                        value={resetEmail}
                        onChange={(event) => setResetEmail(event.target.value)}
                        required
                      />
                    </div>

                    <div className="flex flex-col gap-3 mt-6 md:mt-8">
                      <button
                        onClick={handleResetPassword}
                        disabled={isLoading}
                        className="w-full py-3.5 md:py-4 bg-[#0A3A6B] text-white rounded-2xl font-black text-base md:text-lg hover:bg-[#0c4a8a] transition-all flex items-center justify-center gap-3 h-[54px] md:h-[60px]"
                      >
                        {isLoading ? (
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            <span className="text-sm md:text-base tracking-widest uppercase">SENDING...</span>
                          </div>
                        ) : 'SEND RESET LINK'}
                      </button>
                      <button
                        onClick={() => setOpenResetModal(false)}
                        className="w-full py-3 md:py-4 text-slate-500 dark:text-slate-400 font-bold hover:bg-slate-50 dark:hover:bg-slate-900/50 rounded-2xl transition-all text-xs md:text-sm font-black"
                      >
                        BACK TO LOGIN
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {(errorMessage || successMessage) && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 md:left-auto md:right-8 md:translate-x-0 z-[100] animate-in slide-in-from-bottom-10 duration-500 w-[calc(100%-2rem)] max-w-sm">
          <div className={`${successMessage ? 'bg-green-500' : 'bg-red-500'} text-white p-4 rounded-2xl shadow-2xl flex items-center justify-between border-4 border-white/10`}>
            <p className="font-bold pr-4 flex-1 text-xs md:text-sm">{successMessage || errorMessage}</p>
            <button
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              onClick={handleCloseSnackbar}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showVerificationModal}
        title="Email Verification"
        message="Your email is not verified. Please check your inbox or resend the link below."
        confirmText={resendLoading ? 'Resending...' : 'Resend Email'}
        cancelText="Close"
        onConfirm={handleResendEmail}
        onCancel={() => setShowVerificationModal(false)}
        type="warning"
      />
    </div>
  );
};

export default SignInScreen;
